import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './components/Login';
import Logout from './components/Logout';
import TermsOfService from './components/TermsOfService';
import EventHome from './components/EventHome';
import EventAttendees from './components/EventAttendees';
import EventAgenda from './components/EventAgenda';
import EventSpeakers from './components/EventSpeakers';
import EventSponsors from './components/EventSponsors';
import EventMessages from './components/EventMessages';
import EventRegistration from './components/EventRegistration';
import RegistrationTypeSelection from './components/RegistrationTypeSelection';
import RegistrationForm from './components/RegistrationForm';
import RegistrationSuccess from './components/RegistrationSuccess';
import UserProfile from './components/UserProfile';
import EditUserProfile from './components/EditUserProfile';
import NotificationSettings from './components/NotificationSettings';
import EntityFormSelector from './components/EntityFormSelector';
import EntityProfileForm from './pages/EntityProfileForm';
import EventEntityCompanies from './pages/EventEntityCompanies';
import EventNonprofits from './pages/EventNonprofits';
import EventPhilanthropists from './pages/EventPhilanthropists';
import EventAvailability from './components/EventAvailability';
import EventMeetings from './components/EventMeetings';
import EventConnections from './components/EventConnections';
import EventsList from './pages/EventsList';
import { EVENTS_LIST_PATH } from './config/events';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Login Route */}
            <Route path="/login" element={<Login />} />

            {/* Public Logout Route - clears cache, localStorage, cookies */}
            <Route path="/logout" element={<Logout />} />

            {/* Terms of Service Route - requires auth but skips terms check */}
            <Route
              path="/terms-of-service"
              element={
                <ProtectedRoute skipTermsCheck={true}>
                  <TermsOfService />
                </ProtectedRoute>
              }
            />

            {/* Signed-in landing: every event with the user's registration status */}
            <Route
              path={EVENTS_LIST_PATH}
              element={
                <ProtectedRoute>
                  <EventsList />
                </ProtectedRoute>
              }
            />

            {/* Protected Event Home Route */}
            <Route
              path="/event/:eventNameId/home"
              element={
                <ProtectedRoute>
                  <EventHome />
                </ProtectedRoute>
              }
            />

            {/* Protected User Profile Routes */}
            <Route
              path="/user/:contactId"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/:contactId/edit"
              element={
                <ProtectedRoute>
                  <EditUserProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notification-settings"
              element={
                <ProtectedRoute>
                  <NotificationSettings />
                </ProtectedRoute>
              }
            />

            {/* Protected Event Page Routes */}
            <Route
              path="/event/:eventNameId/attendees"
              element={
                <ProtectedRoute>
                  <EventAttendees />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/agenda"
              element={
                <ProtectedRoute>
                  <EventAgenda />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/speakers"
              element={
                <ProtectedRoute>
                  <EventSpeakers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/sponsors"
              element={
                <ProtectedRoute>
                  <EventSponsors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/messages"
              element={
                <ProtectedRoute>
                  <EventMessages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/availability"
              element={
                <ProtectedRoute>
                  <EventAvailability />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/meetings"
              element={
                <ProtectedRoute>
                  <EventMeetings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event/:eventNameId/meetings/:tab"
              element={
                <ProtectedRoute>
                  <EventMeetings />
                </ProtectedRoute>
              }
            />

            {/* Connections Route */}
            <Route
              path="/event/:eventNameId/connections"
              element={
                <ProtectedRoute>
                  <EventConnections />
                </ProtectedRoute>
              }
            />

            {/* Public Event Registration Routes (no authentication required) */}
            <Route path="/event/:eventNameId/registration" element={<EventRegistration />} />
            <Route path="/event/:eventNameId/registration/type" element={<RegistrationTypeSelection />} />
            <Route path="/event/:eventNameId/registration/form" element={<RegistrationForm />} />
            <Route path="/event/:eventNameId/registration/success" element={<RegistrationSuccess />} />

            {/* Entity Profile Form Routes */}
            <Route
              path="/entity-forms"
              element={
                <ProtectedRoute>
                  <EntityFormSelector />
                </ProtectedRoute>
              }
            />
            {/* Event-scoped profile form (preferred: the event comes from the URL, not from last_event) */}
            <Route
              path="/event/:eventNameId/entity-profile/:entityId/:companyEntityId"
              element={<EntityProfileForm />}
            />
            {/* Legacy route kept for old links; falls back to the last visited event */}
            <Route
              path="/entity-profile/:entityId/:companyEntityId"
              element={<EntityProfileForm />}
            />

            {/* Event Entity Companies Route */}
            <Route
              path="/event/:eventNameId/EventEntity/:eventEntityId"
              element={
                <ProtectedRoute>
                  <EventEntityCompanies />
                </ProtectedRoute>
              }
            />

            {/* Nonprofits Route (EventEntity 1) */}
            <Route
              path="/event/:eventNameId/nonprofits"
              element={
                <ProtectedRoute>
                  <EventNonprofits />
                </ProtectedRoute>
              }
            />

            {/* Philanthropists Route (EventEntity 2) */}
            <Route
              path="/event/:eventNameId/philanthropists"
              element={
                <ProtectedRoute>
                  <EventPhilanthropists />
                </ProtectedRoute>
              }
            />

            {/* Donors Route (F4C 2027+: Donor entity; reuses the philanthropist directory) */}
            <Route
              path="/event/:eventNameId/donors"
              element={
                <ProtectedRoute>
                  <EventPhilanthropists />
                </ProtectedRoute>
              }
            />

            {/* Default redirects */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
